import React, { useState } from 'react';
import api from '../../../services/api';
import useMeta from '../../../hooks/useMeta';
import CustomerCard from '../CustomerCard';
import CustomerMatches from '../CustomerMatches';
import FaceCapture from '../FaceCapture';
import { Notice, Spinner } from '../../common/Feedback';
import { FaceIcon, IdIcon, PhoneIcon, UserIcon } from '../../common/icons';

const MODES = [
  { id: 'phone', label: 'Mobile number', sub: 'Search Nyafaz, Engagement, check-ins & past bills', icon: PhoneIcon },
  { id: 'id', label: 'Customer ID', sub: 'Nyafaz face-user ID', icon: IdIcon },
  { id: 'walkin', label: 'New walk-in', sub: 'Type name and phone', icon: UserIcon },
  { id: 'face', label: 'Scan face', sub: 'Match with the camera', icon: FaceIcon, needsFace: true },
];

/** Step 1 — attach the customer to the bill. */
export default function IdentifyStep({ bill, onIdentified, onContinue }) {
  const meta = useMeta();
  const hasCustomer = Boolean(bill.customer);
  const [mode, setMode] = useState(hasCustomer ? null : 'phone');
  const [phone, setPhone] = useState('');
  const [faceId, setFaceId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState(null);
  const [pickingKey, setPickingKey] = useState('');

  const switchMode = (id) => {
    setMode(id);
    setError('');
    setLookup(null);
  };

  const attach = async (call) => {
    setError('');
    try {
      const updated = await call();
      onIdentified(updated);
      setMode(null);
      setLookup(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const run = async (call) => {
    setBusy(true);
    await attach(call);
    setBusy(false);
  };

  const search = async (digits) => {
    setBusy(true);
    setError('');
    setLookup(null);
    try {
      setLookup(await api.lookupCustomer(digits));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // A match with a Nyafaz id loads the full profile; any other source keeps the name and number it was found with.
  const pick = async (match, key) => {
    setPickingKey(key);
    await attach(() =>
      match.face_user_id
        ? api.identify(bill.id, { face_user_id: match.face_user_id })
        : api.identify(bill.id, { manual: true, name: match.name, phone: match.phone, metadata: { provider: match.source } }),
    );
    setPickingKey('');
  };

  const submit = (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (mode === 'phone') {
      if (digits.length < 10) return setError('Enter a 10-digit mobile number');
      return search(digits);
    }
    if (mode === 'id') {
      if (!faceId.trim()) return setError('Enter the customer ID');
      return run(() => api.identify(bill.id, { face_user_id: faceId.trim() }));
    }
    if (mode === 'walkin') {
      if (!name.trim()) return setError('Enter the customer name');
      return run(() => api.identify(bill.id, { manual: true, name: name.trim(), phone: digits }));
    }
    return undefined;
  };

  const modes = MODES.filter((m) => !m.needsFace || meta?.face_enabled);

  return (
    <div className="dp-step-body">
      <div className="dp-step-head">
        <h2>Customer</h2>
        <p className="dp-help">
          {hasCustomer ? 'This customer is on the bill. Continue, or look up someone else.' : 'Find the customer by phone, or bill a new walk-in.'}
        </p>
      </div>

      {hasCustomer ? (
        <div className="dp-identified">
          <CustomerCard bill={bill} />
          <div className="dp-row">
            <button type="button" className="dp-btn" onClick={onContinue}>
              Continue to services
            </button>
            {!mode ? (
              <button type="button" className="dp-btn dp-btn--ghost" onClick={() => switchMode('phone')}>
                Look up someone else
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {mode ? (
        <>
          <div className="dp-choice-grid">
            {modes.map(({ id, label, sub, icon: Icon }) => (
              <button key={id} type="button" className={`dp-choice${mode === id ? ' is-on' : ''}`} onClick={() => switchMode(id)}>
                <Icon size={22} />
                <strong>{label}</strong>
                <small>{sub}</small>
              </button>
            ))}
          </div>

          {mode === 'face' ? (
            <FaceCapture busy={busy} onCapture={(blob) => run(() => api.identifyFace(bill.id, blob))} />
          ) : (
            <form className="dp-form" onSubmit={submit}>
              {mode === 'walkin' ? (
                <label className="dp-field">
                  <span>Full name</span>
                  <input className="dp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" autoFocus />
                </label>
              ) : null}
              {mode === 'id' ? (
                <label className="dp-field">
                  <span>Customer ID</span>
                  <input className="dp-input" value={faceId} onChange={(e) => setFaceId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" autoFocus />
                </label>
              ) : (
                <label className="dp-field">
                  <span>Mobile number{mode === 'walkin' ? ' (optional)' : ''}</span>
                  <div className="dp-phone">
                    <em>+91</em>
                    <input
                      className="dp-input"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/[^\d\s]/g, '').slice(0, 14));
                        if (mode === 'phone') setLookup(null);
                      }}
                      placeholder="98765 43210"
                      autoFocus={mode === 'phone'}
                    />
                  </div>
                </label>
              )}
              <button type="submit" className="dp-btn" disabled={busy}>
                {busy ? <Spinner small /> : null}
                {mode === 'walkin' ? 'Add customer' : 'Find customer'}
              </button>
            </form>
          )}

          {mode === 'phone' && busy ? (
            <p className="dp-help">Searching Nyafaz customers, Demo Engagement, branch check-ins and past POS bills…</p>
          ) : null}
          {mode === 'phone' && lookup ? (
            <CustomerMatches result={lookup} busyKey={pickingKey} onPick={pick} onNewWalkIn={() => switchMode('walkin')} />
          ) : null}
          <Notice tone="error">{error}</Notice>
        </>
      ) : null}
    </div>
  );
}
